import React from "react";
import { InputSwitch } from "primereact/inputswitch";
import { PortalDsBadge } from "src/components/portal-design-system/portal-ds-badge";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "src/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency, formatDate } from "../../utils/billing-utils";
import type {
  CommissionInvoice,
  Referral,
} from "../types/commissions.types";
import { PortalDsEmptyState } from "src/components/portal-design-system/portal-ds-empty-state";

interface CommissionInvoiceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: CommissionInvoice | null;
  referrals: Referral[];
  onToggleIncludeInTotal?: (invoice: CommissionInvoice, includeInTotal: boolean) => void;
}

const CommissionInvoiceDrawer: React.FC<CommissionInvoiceDrawerProps> = ({
  open,
  onOpenChange,
  invoice,
  referrals,
  onToggleIncludeInTotal,
}) => {
  if (!invoice) return null;

  const referralById = referrals.reduce<Record<string, Referral>>(
    (acc, r) => {
      acc[r.id] = r;
      return acc;
    },
    {}
  );

  const assignments = invoice.referralAssignments ?? [];
  const lines = invoice.lines ?? [];

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="!w-1/2 !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle className={portalDs.sheetTitle}>
            Invoice —
            {invoice.invoiceNumber}
          </SheetTitle>
        </SheetHeader>
        <div className="grid gap-6 py-4">
          <section className="grid gap-3">
            <div className="grid grid-cols-[minmax(0,8rem)_1fr] gap-x-4 gap-y-1 items-baseline">
              <span className={cn(portalDs.label, portalDs.tableCellMuted)}>Invoice #</span>
              <span className={portalDs.bodySm}>{invoice.invoiceNumber}</span>
              <span className={cn(portalDs.label, portalDs.tableCellMuted)}>Company</span>
              <span className={portalDs.bodySm}>{invoice.companyName}</span>
              <span className={cn(portalDs.label, portalDs.tableCellMuted)}>Amount</span>
              <span className={cn(portalDs.bodySm, "font-medium")}>
                {formatCurrency(invoice.amount)}
              </span>
              <span className={cn(portalDs.label, portalDs.tableCellMuted)}>Invoice date</span>
              <span className={portalDs.bodySm}>
                {invoice.createdAt ? formatDate(invoice.createdAt) : "—"}
              </span>
              <span className={cn(portalDs.label, portalDs.tableCellMuted)}>Paid date</span>
              <span className={portalDs.bodySm}>
                {invoice.paidDate ? formatDate(invoice.paidDate) : "—"}
              </span>
              <span className={cn(portalDs.label, portalDs.tableCellMuted)}>Status</span>
              <span className="text-sm">
                <PortalDsBadge
                  pill
                  variant={invoice.status === "paid" ? "green" : "amber"}
                >
                  {invoice.status === "paid" ? "Paid" : "Unpaid"}
                </PortalDsBadge>
              </span>
              <span className={cn(portalDs.label, portalDs.tableCellMuted)}>Include in total</span>
              <span className="text-sm flex items-center gap-2">
                <InputSwitch
                  checked={invoice.includeInTotal !== false}
                  onChange={(e) =>
                    onToggleIncludeInTotal?.(invoice, e.value ?? true)
                  }
                  disabled={!onToggleIncludeInTotal}
                />
              </span>
            </div>
          </section>

          {assignments.length > 0 && (
            <section>
              <h3 className={cn(portalDs.sheetSectionTitle, "mb-2")}>
                Referrals assigned
              </h3>
              <ul className={cn(portalDs.tableContainer, "divide-y divide-[#EDEEF2]")}>
                {assignments.map((a) => {
                  const ref = referralById[a.referralId];
                  return (
                    <li
                      key={a.referralId}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span className={cn(portalDs.tableCell, "font-medium px-0 py-0")}>
                        {ref?.organizationName ?? a.referralId}
                      </span>
                      <span className={portalDs.tableCellMuted}>
                        Rate: {a.rate}% · Payout: {formatCurrency(a.payoutAmount)}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className={cn(portalDs.caption, "mt-1")}>
                Total paid out:{" "}
                {formatCurrency(
                  assignments.reduce((sum, a) => sum + a.payoutAmount, 0)
                )}
              </p>
            </section>
          )}

          {assignments.length === 0 && invoice.referralId && (
            <section>
              <h3 className={cn(portalDs.sheetSectionTitle, "mb-2")}>
                Referrals assigned
              </h3>
              <p className={portalDs.bodySm}>
                No referral assignments with rate/payout on this invoice.
              </p>
            </section>
          )}

          <section>
            <h3 className={cn(portalDs.sheetSectionTitle, "mb-2")}>
              Line items
            </h3>
            {lines.length > 0 ? (
              <div className={portalDs.tableContainer}>
                <Table>
                  <TableHeader>
                    <TableRow className={portalDs.tableHead}>
                      <TableHead className={portalDs.tableHeadCell}>Name</TableHead>
                      <TableHead className={portalDs.tableHeadCell}>SKU</TableHead>
                      <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
                        Amount
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow
                        key={line.id}
                        className={portalDs.tableBodyRow}
                      >
                        <TableCell className={portalDs.tableCell}>{line.name}</TableCell>
                        <TableCell className={portalDs.tableCell}>{line.sku}</TableCell>
                        <TableCell className={cn(portalDs.tableCell, "text-right tabular-nums")}>
                          {formatCurrency(line.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className={portalDs.tableContainer}>
                <PortalDsEmptyState
                  title="No line items"
                  description="This invoice has no line items to display."
                  className="py-10"
                />
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CommissionInvoiceDrawer;
